import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, UploadCloud, X, ImagePlus } from "lucide-react";
import MediaPicker from "./MediaPicker";

/**
 * ImageUpload — single-file image uploader → posts to /api/admin/media/upload,
 * returns the public URL and calls onChange(url). Also supports picking an
 * existing asset from the Media Library.
 */
export default function ImageUpload({ value, onChange, folder = "uploads", label = "Upload image", testId = "image-upload" }) {
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large (max 15 MB)");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", folder);
    try {
      const { data } = await api.post("/admin/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={testId} className="rounded-2xl border border-dashed border-[#E5E7EB] p-4 bg-[#F8FAFC]">
      {value ? (
        <div className="flex items-start gap-3">
          <img src={value} alt="" className="w-24 h-16 object-cover rounded-lg border border-[#E5E7EB]" />
          <div className="flex-1 min-w-0">
            <div className="text-xs text-muted-foreground truncate">{value}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" onClick={() => inputRef.current?.click()}
                className="text-xs px-3 h-8 rounded-full border border-[#E5E7EB] bg-white hover:bg-[#F3F6FA]" data-testid={`${testId}-replace`}>
                Replace
              </button>
              <button type="button" onClick={() => setPicking(true)}
                className="text-xs px-3 h-8 rounded-full border border-[#E5E7EB] bg-white hover:bg-[#F3F6FA] inline-flex items-center gap-1" data-testid={`${testId}-pick`}>
                <ImagePlus className="w-3 h-3"/> Library
              </button>
              <button type="button" onClick={() => onChange?.("")}
                className="text-xs px-3 h-8 rounded-full border border-[#E5E7EB] bg-white hover:bg-[#F3F6FA] text-rose-600 inline-flex items-center gap-1" data-testid={`${testId}-clear`}>
                <X className="w-3 h-3" /> Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-2">
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
            className="h-24 rounded-xl bg-white border border-[#E5E7EB] flex flex-col items-center justify-center gap-1 text-sm text-muted-foreground hover:bg-[#F3F6FA]" data-testid={`${testId}-picker`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-5 h-5" />}
            <span>{busy ? "Uploading…" : label}</span>
          </button>
          <button type="button" onClick={() => setPicking(true)}
            className="h-24 rounded-xl bg-white border border-[#E5E7EB] flex flex-col items-center justify-center gap-1 text-sm text-muted-foreground hover:bg-[#F3F6FA]" data-testid={`${testId}-pick`}>
            <ImagePlus className="w-5 h-5"/>
            <span>Pick from library</span>
          </button>
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testId}-input`} />
      <MediaPicker open={picking} onClose={() => setPicking(false)} onPick={(url) => onChange?.(url)} folder={folder}/>
    </div>
  );
}
